//src/admin/pages/AdminMakeSale.js
import React, { useEffect, useState } from "react";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import {
  getAdminProducts,
  getAdminCategoriesWithProducts,
  makeProductsOnSale,
} from "../../services/adminProducts.api";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaCheckCircle, FaTimes } from "react-icons/fa";

export default function AdminMakeSale() {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [salePercent, setSalePercent] = useState(10);
  const [selected, setSelected] = useState([]);

  const [toast, setToast] = useState(null); // { type: "success"|"error", text: string }

  useEffect(() => {
    getAdminProducts().then((data) => {
      setProducts(data.filter((p) => !p.on_sale));
    });

    getAdminCategoriesWithProducts().then(setCategories);
  }, []);

  const filtered = products.filter((p) => {
    const q = search.toLowerCase();

    const matchSearch =
      (p.name_en || "").toLowerCase().includes(q) ||
      (p.public_id || "").toLowerCase().includes(q);

    const productCategories = Array.isArray(p.categories)
  ? p.categories
  : typeof p.categories === "string"
  ? p.categories.split(",")
  : [];

const matchCategory = !category || productCategories.includes(category);


    return matchSearch && matchCategory;
  });

  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const applySale = async () => {
    if (!salePercent || selected.length === 0) return;

    try {
      await makeProductsOnSale(selected, salePercent);

      setToast({
        type: "success",
        text: "Sale applied successfully ✅",
      });

      // remove updated products from the list
      setProducts((prev) => prev.filter((p) => !selected.includes(p.id)));
      setSelected([]);

      // auto-hide toast
      setTimeout(() => setToast(null), 2500);
    } catch (e) {
      setToast({
        type: "error",
        text: "Failed to apply sale. Please try again.",
      });
      setTimeout(() => setToast(null), 3000);
    }
  };

const allFilteredIds = filtered.map((p) => p.id);

const isAllSelected =
  allFilteredIds.length > 0 &&
  allFilteredIds.every((id) => selected.includes(id));

const toggleSelectAll = () => {
  if (isAllSelected) {
    // unselect only filtered ones
    setSelected((prev) =>
      prev.filter((id) => !allFilteredIds.includes(id))
    );
  } else {
    // add missing filtered ones
    setSelected((prev) => [
      ...prev,
      ...allFilteredIds.filter((id) => !prev.includes(id)),
    ]);
  }
};


  return (
    <div className="flex bg-gray-100 min-h-screen">
      <AdminSidebar />
      <div className="flex-1 lg:ml-64">
        <AdminHeader />

        <main className="p-4 sm:p-6 space-y-5">

  {/* TOP BAR */}
  <div className="flex items-center justify-between">
    <button
      onClick={() => navigate("/admin/products/on-sale")}
      className="
        flex items-center gap-2
        h-8 px-3
        bg-white
        border border-gray-300
        rounded-lg
        text-sm font-medium
        shadow-[0_1px_0_rgba(0,0,0,0.05)]
        hover:bg-gray-50
      "
    >
      <FaArrowLeft className="text-[12px]" />
      Back
    </button>

    <div className="text-sm text-gray-500">
      Total: <span className="font-semibold text-gray-900">{products.length}</span> ·
      Showing: <span className="font-semibold text-gray-900">{filtered.length}</span>
    </div>
  </div>

  {/* TOAST (slightly refined Shopify style) */}
  {toast && (
    <div className="fixed top-6 right-6 z-[70] animate-fade-in">
      <div className="rounded-[14px] border bg-white shadow-[0_12px_30px_rgba(0,0,0,0.12)] px-4 py-3 flex items-start gap-3">

        <div className={`mt-0.5 ${toast.type === "success" ? "text-green-600" : "text-red-600"}`}>
          {toast.type === "success" ? <FaCheckCircle /> : <FaTimes />}
        </div>

        <div className="text-sm text-gray-900 font-medium">
          {toast.text}
        </div>

        <button
          className="ml-2 text-gray-400 hover:text-gray-600"
          onClick={() => setToast(null)}
        >
          ✕
        </button>

      </div>
    </div>
  )}

  {/* MAIN CARD */}
  <div className="
    bg-white
    border border-[#e3e3e3]
    rounded-[14px]
    shadow-[0_12px_32px_rgba(0,0,0,0.10),0_2px_6px_rgba(0,0,0,0.06)]
    overflow-hidden
  ">

    {/* HEADER CONTROLS (Shopify style) */}
    <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b bg-white">

      {/* SEARCH */}
      <div className="
        flex items-center gap-2
        h-9 px-3
        border border-gray-300
        rounded-lg
        bg-white
        shadow-sm
      ">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or ID"
          className="outline-none text-sm w-[180px]"
        />
      </div>

      {/* CATEGORY */}
      <select
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        className="
          h-9 px-3
          border border-gray-300
          rounded-lg
          bg-white
          text-sm
        "
      >
        <option value="">All categories</option>
        {categories.map((c) => (
          <option key={c.id} value={c.slug}>
            {c.name_en}
          </option>
        ))}
      </select>

      {/* SALE % */}
      <input
        type="number"
        min="1"
        max="90"
        value={salePercent}
        onChange={(e) => setSalePercent(e.target.value)}
        className="
          h-9 w-[100px]
          px-3
          border border-gray-300
          rounded-lg
          text-sm
        "
        placeholder="Sale %"
      />

      {/* APPLY BUTTON */}
      <button
        onClick={applySale}
        className="
          h-9 px-4
          bg-[#202223]
          text-white
          rounded-lg
          text-sm font-medium
          shadow-[0_1px_0_rgba(0,0,0,0.05),0_2px_4px_rgba(0,0,0,0.08)]
          hover:bg-black
          transition
        "
      >
        Apply Sale
      </button>

    </div>

    {/* TABLE */}
    <div className="overflow-x-auto">
      <table className="w-full min-w-[600px] text-center">

        <thead className="bg-[#f6f6f7] text-[#6d7175] text-[13px] font-medium">
          <tr>
            <th className="px-4 py-2">
              <input
                type="checkbox"
                className="w-4 h-4 accent-black"
                checked={isAllSelected}
                onChange={toggleSelectAll}
              />
            </th>
            <th className="px-4 py-2 text-left">Name</th>
            <th className="px-4 py-2 text-left">Public ID</th>
            <th className="px-4 py-2 text-left">Price</th>
          </tr>
        </thead>

        <tbody>
          {filtered.map((p) => (
            <tr
              key={p.id}
              className="border-b hover:bg-[#f6f6f7] transition text-[13px]"
            >
              <td className="px-4 py-2">
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-black"
                  checked={selected.includes(p.id)}
                  onChange={() => toggle(p.id)}
                />
              </td>

              <td className="px-4 py-2 text-left font-medium">
                {p.name_en}
              </td>

              <td className="px-4 py-2 text-left text-gray-500">
                {p.public_id}
              </td>

              <td className="px-4 py-2 text-left">
                ${Number(p.base_price).toFixed(2)}
              </td>
            </tr>
          ))}

          {filtered.length === 0 && (
            <tr>
              <td colSpan="4" className="p-6 text-center text-gray-500">
                No products found.
              </td>
            </tr>
          )}
        </tbody>

      </table>
    </div>

  </div>

</main>
      </div>
    </div>
  );
}

